import { Component, EventEmitter, OnInit } from "@angular/core";
import { AppConfigService } from "../config/app-config.service";
import { Property } from "../shared/property/property.model";
import { ActivatedRoute } from "@angular/router";
import { DescriptorEnum } from "../config/descriptors/descriptor.enum";
import { DescriptorService } from "./descriptor.service";
import { AppUtils } from "../shared/app-utils";
import { ConfigStructure } from "../config/config-structure.model";
import { TranslatorService } from "../shared/translator.service";
import { BingMapMarker } from "../bing-maps/bing-map-marker";
import { QueryParams } from "../shared/list/query-params.model";
import { SearchService } from "../search/search.service";
import { SearchRequest } from "../search/search-request.model";
import { RichSearchList } from "../search/search-list/rich-search-list";
import { FilterQueryConfig } from "../config/filters/filter-query-config.model";
import { DescriptorTab } from "./descriptor-tab.enum";
import { FilterPortalConfig } from "../config/filters/filter-portal-config.model";
import { FilterSearch } from "../search/search-list/filter-search";
import { FacetService } from "../shared/list/facets/facet.service";
import { Filter } from "../search/search-list/filters/filter-block/filter-item/filter";
import { FiltersService } from "../search/search-list/filters/filters.service";
import { environment } from "../../environments/environment";
import { find, omit } from "lodash";
import { Title } from "@angular/platform-browser";

@Component({
  selector: "app-descriptor",
  templateUrl: "./descriptor.component.html",
  styleUrls: ["./descriptor.component.css"],
})
export class DescriptorComponent implements OnInit {
  properties: Property[] = [];

  descriptorName: Property;

  descriptorType: DescriptorEnum;

  descriptorNlpId: string;

  alt: string;

  coordinates: BingMapMarker[];

  searchResultAuthors: RichSearchList;

  searchResultTopic: RichSearchList;

  searchResultGenre: RichSearchList;

  searchResultPublicDataset: RichSearchList;

  isWikiDataAvaible = false;

  inProgress: boolean;

  choosenTab: string;

  mainAppConfig;

  ready = new EventEmitter<boolean>();

  filterHideInResult: Filter[] = [];

  viafId = null;

  public defaultSrc;
  public imageAuthor;
  public wikiData;
  public wikiDataId;

  constructor(
    public appConfig: AppConfigService,
    private route: ActivatedRoute,
    private descriptorService: DescriptorService,
    private translatorService: TranslatorService,
    private searchService: SearchService,
    private facetsService: FacetService,
    private titleService: Title
  ) {}

  ngOnInit() {
    this.route.params.subscribe(
      (params: { id: string; type: DescriptorEnum }) => {
        this.mainAppConfig = this.appConfig.getConfig();
        this.searchResultAuthors = null;
        this.searchResultTopic = null;
        this.searchResultGenre = null;
        this.choosenTab = null;
        this.descriptorType = params.type;
        this.descriptorNlpId = params.id;
        this.ready.emit(false);
        this.descriptorService.getProperties(params.id).subscribe((data) => {
          this.properties = AppUtils.mapObjectToProperties(data);
          const bnId = data["nlpId"];
          const externalIds = data["externalIds"];
          this.viafId = null;
          if (externalIds) {
            externalIds.forEach((externalId) => {
              if (externalId["dataAssociated"] === "viaf") {
                this.viafId = externalId["stringValue"];
              }
            });
          }
          const language = localStorage.getItem("LANGUAGE")
            ? localStorage.getItem("LANGUAGE").toLowerCase()
            : "pl";
          this.descriptorService
            .getWikiDataData(
              bnId || null,
              this.viafId || null,
              this.descriptorType,
              language
            )
            .subscribe(
              (wikiData) => {
                this.mapWikiData(wikiData);
              },
              (error) => {
                this.wikiData = null;
                this.isWikiDataAvaible = false;
                this.defaultSrc = null;
                this.imageAuthor = null;
              }
            );
          this.descriptorName = this.getDescriptorName();
          this.titleService.setTitle("Omnis - " + this.descriptorName.value);
          this.alt = this.getAlt(data.work_title_alt && data.work_title_alt[0]);
          this.coordinates = [this.getCoordinates()];
          this.filterHideInResult = [];
          this.search(
            new QueryParams({
              page: 0,
              pageSize: this.mainAppConfig.defaultPageSize,
              searchInText: false,
            }),
            DescriptorTab.AUTHORS,
            false
          );
          this.search(
            new QueryParams({
              page: 0,
              pageSize: this.mainAppConfig.defaultPageSize,
              searchInText: false,
            }),
            DescriptorTab.TOPIC,
            false
          );
          this.search(
            new QueryParams({
              page: 0,
              pageSize: this.mainAppConfig.defaultPageSize,
              searchInText: false,
            }),
            DescriptorTab.GENRE,
            false
          );
          this.search(
            new QueryParams({
              page: 0,
              pageSize: this.mainAppConfig.defaultPageSize,
              searchInText: false,
            }),
            DescriptorTab.PUBLIC_DATASET,
            false
          );
        });
      }
    );
  }

  public genreExists() {
    return (
      this.descriptorType === DescriptorEnum.GENRE &&
      this.searchResultGenre &&
      this.searchResultGenre.search &&
      this.searchResultGenre.search.length > 0
    );
  }

  public publicExists() {
    return (
      this.searchResultPublicDataset &&
      this.searchResultPublicDataset.search &&
      this.searchResultPublicDataset.search.length > 0
    );
  }

  public topicExists() {
    return (
      this.searchResultTopic &&
      this.searchResultTopic.search &&
      this.searchResultTopic.search.length > 0
    );
  }

  public authorsExists() {
    return (
      (this.descriptorType === DescriptorEnum.PERSONAL ||
        this.descriptorType === DescriptorEnum.CORPORATE ||
        this.descriptorType === DescriptorEnum.EVENT) &&
      this.searchResultAuthors &&
      this.searchResultAuthors.search &&
      this.searchResultAuthors.search.length > 0
    );
  }

  private resolveActiveTab() {
    if (this.authorsExists()) {
      this.choosenTab = DescriptorTab.AUTHORS;
    } else {
      if (this.topicExists()) {
        this.choosenTab = DescriptorTab.TOPIC;
      } else if (this.genreExists()) {
        this.choosenTab = DescriptorTab.GENRE;
      }
    }
  }

  mapWikiData(data) {
    if (data.image && data.image.imageType === "svg") {
      this.defaultSrc = "data:image/svg+xml;base64," + data.image.encodedImage;
    } else if (data.image && data.image.imageType) {
      this.defaultSrc =
        "data:image/" +
        data.image.imageType +
        ";base64," +
        data.image.encodedImage;
    } else {
      this.defaultSrc = null;
    }
    this.isWikiDataAvaible = true;
    this.imageAuthor = data.image ? data.image.author : null;
    this.wikiData = this.keepKeyOrder(data.bindings);
    this.wikiDataId = data.id;
  }

  keepKeyOrder(obj) {
    if (typeof obj === "object" && !Array.isArray(obj)) {
      let transformKey = (k) => [k, this.keepKeyOrder(obj[k])];
      return Object.keys(obj).map(transformKey);
    } else {
      return obj;
    }
  }

  getDescriptorName(): Property {
    return this.translatorService.find(this.properties, "preferredName");
  }

  getConfig(): ConfigStructure[] {
    return this.appConfig.descriptorConfig.getDescriptorConfig(
      this.descriptorType
    );
  }

  getImagePath(wcga: boolean): string {
    return wcga === true
      ? `assets/img/${this.descriptorType}_wcga.svg`
      : `assets/img/${this.descriptorType}.svg`;
  }

  getAlt(data): string {
    return data !== undefined && data !== "" ? data : "";
  }

  getCoordinates(): BingMapMarker {
    const latProp = this.translatorService.find(
      this.properties,
      "descr_geometrya.lat"
    );
    const lonProp = this.translatorService.find(
      this.properties,
      "descr_geometrya.lon"
    );

    if (latProp && lonProp) {
      return new BingMapMarker(latProp.value, lonProp.value);
    }
    return null;
  }

  search(
    queryParams: QueryParams,
    type: DescriptorTab | string,
    clear: boolean = true
  ) {
    if (clear) {
      this.filterHideInResult = [new Filter({ field: "_type", value: "work" })];
    } else {
      this.filterHideInResult.push(
        new Filter({ field: "_type", value: "work" })
      );
    }

    let additionalFiltersString;
    if (type === DescriptorTab.PUBLIC_DATASET) {
      additionalFiltersString = "_type:public_dataset";
      additionalFiltersString = this.append(
        additionalFiltersString,
        "filter_subject",
        this.getDescriptorName().value
      );
    } else {
      additionalFiltersString = "_type:work";
    }

    const descriptorFacetConfig =
      this.appConfig.descriptorFacetConfig.getConfig(type);
    const configuredFilter = descriptorFacetConfig.getFilterByType(
      this.descriptorType
    );

    if (configuredFilter) {
      additionalFiltersString = this.append(
        additionalFiltersString,
        configuredFilter,
        this.getDescriptorName().value
      );
    } else {
      additionalFiltersString = this.append(
        additionalFiltersString,
        "_id",
        "0"
      );
    }

    let s = queryParams.filters
      ? queryParams.filters.concat("," + additionalFiltersString)
      : additionalFiltersString;
    let searchRequest = new SearchRequest({
      page: +queryParams.page - 1,
      phrase: queryParams.phrase,
      filter: s,
      sort: queryParams.sort,
      textOnly: queryParams.searchInText,
      pageSize: +queryParams.pageSize,
    });

    let aggregates = FiltersService.getAggregate(
      this.appConfig.descriptorFacetConfig.getConfig(type).facets.filtersConfig
    );

    this.inProgress = true;
    this.searchService.findAll(searchRequest, aggregates).subscribe(
      (value) => {
        value.facets
          .filter(
            (facet) =>
              !!this.filterHideInResult.find(
                (filter) =>
                  facet.field == filter.field && facet.value == filter.value
              )
          )
          .forEach((facet) => {
            facet.hideInResult = true;
          });
        value.filters
          .filter(
            (facet) =>
              !!this.filterHideInResult.find(
                (filter) =>
                  facet.field == filter.field && facet.value == filter.value
              )
          )
          .forEach((facet) => {
            facet.hideInResult = true;
          });
        switch (type) {
          case DescriptorTab.AUTHORS:
            this.searchResultAuthors = value;
            break;
          case DescriptorTab.GENRE:
            this.searchResultGenre = value;
            break;
          case DescriptorTab.TOPIC:
            this.searchResultTopic = value;
            break;
          case DescriptorTab.PUBLIC_DATASET:
            this.searchResultPublicDataset = value;
            break;
        }
        if (
          this.searchResultAuthors &&
          this.searchResultTopic &&
          this.searchResultGenre
        ) {
          this.ready.emit(true);
          if (!this.choosenTab) {
            this.resolveActiveTab();
          }
          this.inProgress = false;
        }
      },
      (error1) => {
        this.inProgress = false;
        console.log(error1);
      }
    );
  }

  private append(
    additionalFiltersString: string,
    name: string,
    value: string
  ): string {
    this.filterHideInResult.push(new Filter({ field: name, value: value }));
    if (additionalFiltersString === "") {
      return additionalFiltersString.concat(name + ":`" + value + "`");
    } else {
      return additionalFiltersString.concat("," + name + ":`" + value + "`");
    }
  }
}
